/// <amd-module name="Wit/WorkItemModel/Services/WorkItemSavingService" />
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { CoreField, CoreFieldRefNames, DalFields } from "Wit/Common/Generated/Constants";
import { WorkItemStoreLevelWITDataSource } from "Wit/WorkItemDataManager/Sources/WorkItemStoreLevelWITDataSource";
import { IWorkItemDataManager } from "Wit/WorkItemDataManager/WorkItemDataManager";
import { ErrorCodes, Exceptions, PageSizes, WorkItemUpdateResultState } from "Wit/WorkItemModel/OM";
import {
    IDataSourceResponse,
    IField,
    ILinkUpdates,
    IRouteParams,
    IWorkItem,
    IWorkItemBulkSaveResult,
    IWorkItemChangedArgs,
    IWorkItemSavingFailedError,
    IWorkItemsBulkSaveSuccessResult,
    IWorkItemUpdate,
    IWorkItemUpdatePackage,
    IWorkItemUpdateResult
} from "Wit/WorkItemModel/OM/WorkItemInterfaces";

import { IFieldDataDictionary, ILinkInfo, IWorkItemData, LinkCommand } from "Wit/WorkItemDataManager/Contracts";
import * as Resources from "Wit/WorkItemModel/Resources";
import { compareFieldValues, convertFieldValueToExternal, isUserChange } from "Wit/WorkItemModel/WorkItem/FieldUtils";
import { WorkItemLink } from "Wit/WorkItemModel/WorkItem/Links";

export interface IWorkItemSavingService extends IVssService {
    /**
     * Save the provided work items.
     * @param workItems
     * @param keepDeletedLinks Flag indicating skip of cleaning up deleted links from the list of workItem links
     * @param ignoreWorkItemUpdateTelemetry Flag indicating whether to not send telemetry for individual work item update (useful if you are already sending telemetry and potentially saving lots of work items)
     */
    saveWorkItemsBatchAsync(
        workItems: IWorkItem[],
        optionalParams?: {
            keepDeletedLinks?: boolean,
            ignoreWorkItemUpdateTelemetry?: boolean
        }
    ): Promise<IWorkItemsBulkSaveSuccessResult>;

    /**
     * Begin saving the provided work items.  If saving any of the work items fails, the
     * rest of the work items will be saved.  If there are resolvable merge conflicts with
     * any items being saved, the merged work item will be saved
     * @param workItems Work items to be saved
     * @param callback success callback
     * @param errorCallback error callback
     * @param pageCallback success callback invoked after saving each page
     * @param keepDeletedLinks Flag indicating skip of cleaning up deleted links from the list of workItem links
     * @param ignoreWorkItemUpdateTelemetry Flag indicating whether to not send telemetry for individual work item update (useful if you are already sending telemetry and potentially saving lots of work items)
     */
    beginSaveWorkItemsBatch(
        workItems: IWorkItem[],
        callback: (result: any) => void,
        errorCallback?: (error: any) => void,
        keepDeletedLinks?: boolean,
        ignoreWorkItemUpdateTelemetry?: boolean
    ): void;

    saveWorkItemLinksAsync(workItem: IWorkItem, links: WorkItemLink[], fieldsToClear?: CoreField[]): Promise<IWorkItemBulkSaveResult[]>;
}

class WorkItemSavingService extends VssService implements IWorkItemSavingService {
    private witDataManager: IWorkItemDataManager;

    public _serviceStart(pageContext: IVssPageContext) {
        this.witDataManager = pageContext.getService<IWorkItemDataManager>("IWorkItemDataManager");
        this.pageContext = pageContext;
    }

    public saveWorkItemsBatchAsync(
        workItems: IWorkItem[],
        optionalParams?: {
            keepDeletedLinks?: boolean,
            ignoreWorkItemUpdateTelemetry?: boolean
        }
    ): Promise<IWorkItemsBulkSaveSuccessResult> {
        const { keepDeletedLinks, ignoreWorkItemUpdateTelemetry } = optionalParams || {};
        return new Promise((resolve, reject) => {
            this.beginSaveWorkItemsBatch(
                workItems,
                result => resolve(result),
                error => reject(error),
                keepDeletedLinks,
                ignoreWorkItemUpdateTelemetry
            );
        });
    }

    public beginSaveWorkItemsBatch(
        workItems: IWorkItem[],
        callback: (result: IWorkItemsBulkSaveSuccessResult) => void,
        errorCallback?: (error: IWorkItemSavingFailedError) => void,
        keepDeletedLinks?: boolean,
        ignoreWorkItemUpdateTelemetry?: boolean
    ) {
        const service = this;
        const validWorkItems: IWorkItem[] = [];
        const workItemsQueue: IWorkItem[][] = [];
        const mergedWorkItemFieldChanges: { [id: number]: { [key: string]: IField } } = {};
        const results: IWorkItemBulkSaveResult[] = [];
        let hasError = false;
        const mergedWorkItems: { [id: number]: boolean } = {};
        const tempIdMap: { [id: number]: number } = {};

        workItems.forEach(workItem => {
            if (workItem.isValid()) {
                validWorkItems.push(workItem);
            } else {
                hasError = true;
                results.push({
                    workItem: workItem,
                    error: {
                        name: Exceptions.WorkItemSaveFailedDueToInvalidStatusException,
                        message: workItem.infoText.value.text,
                        workItem: workItem
                    } as Error
                });
            }
        });

        if (validWorkItems.length > 0) {
            workItemsQueue.push(validWorkItems);
        }

        const beginSave = () => {
            if (workItemsQueue.length > 0) {
                let currentWorkItems = workItemsQueue.pop() || [];

                if (currentWorkItems.length > PageSizes.SAVE) {
                    workItemsQueue.push(currentWorkItems.slice(PageSizes.SAVE));
                    currentWorkItems = currentWorkItems.slice(0, PageSizes.SAVE);
                }

                if (currentWorkItems.length > PageSizes.SAVE) {
                    throw new Error("Too many work items for batch save.");
                }

                service._beginSaveWorkItemsInternal(
                    currentWorkItems,
                    tempIdMap,
                    mergedWorkItemFieldChanges,
                    () => {
                        currentWorkItems.forEach((workItem: IWorkItem) => {
                            results.push({ workItem: workItem });
                        });

                        window.setTimeout(() => {
                            beginSave();
                        }, 0);
                    },
                    (error: IWorkItemSavingFailedError) => {
                        let mergingWorkItemsCount = 0;
                        let hasMergeErrors = false;
                        let hasNonMergeErrors = false;

                        /**
                         * Starts the next save if all merges have completed
                         * @param workItemMerged Indicates if the work item triggering the next save is the result of a successful merge
                         */
                        const tryStartNextSave = (workItemMerged: boolean) => {
                            // If there are no work items being merged, kick off the next save attempt.
                            if (mergingWorkItemsCount === 0) {
                                if (currentWorkItems.length === 1 && workItemMerged) {
                                    // There is only one work item, but it was successfuly merged so try saving again.
                                    workItemsQueue.push(currentWorkItems);
                                } else if (currentWorkItems.length > 1) {
                                    const mid = currentWorkItems.length / 2;

                                    workItemsQueue.push(currentWorkItems.slice(mid));
                                    workItemsQueue.push(currentWorkItems.slice(0, mid));
                                } else if (error.results) {
                                    results.push(error.results[0]);
                                }

                                window.setTimeout(() => {
                                    beginSave();
                                }, 0);
                            }
                        };

                        const mergeCompleted = (workitem: IWorkItem, changedFields: { [id: string]: IField }) => {
                            mergingWorkItemsCount -= 1;

                            changedFields = { ...changedFields, ...mergedWorkItemFieldChanges[workitem.id] };

                            // Try and start the next save indicating that the work item was successfully merged.
                            tryStartNextSave(true);
                        };

                        const mergeError = () => {
                            mergingWorkItemsCount -= 1;
                            hasError = true;

                            // Try and start the next save.
                            tryStartNextSave(false);
                        };

                        if (error && error.name === Exceptions.WorkItemBulkSaveException) {
                            const saveResults = error.results;

                            if (saveResults) {
                                // Attempt to resolve any work item conflicts.
                                for (const result of saveResults) {
                                    const workItem = result.workItem;

                                    // If this is a work item updated error and we have not already tried to merge this work item, try merging.
                                    if (
                                        result.error &&
                                        (result.error.errorCode === ErrorCodes.WorkItemMissingOrUpdated ||
                                            result.error.errorCode === ErrorCodes.ResourceLinkNotFoundException) &&
                                        !mergedWorkItems.hasOwnProperty(workItem.id.toString())
                                    ) {
                                        hasMergeErrors = true;

                                        // Add the work item to the set of work items we have tried merging.
                                        mergedWorkItems[workItem.id] = true;

                                        // Keep track of how many work items are being merged so we know when
                                        // we are all done.
                                        mergingWorkItemsCount += 1;

                                        service._beginTryMergeWorkItem(workItem, mergeCompleted, mergeError);
                                    } else {
                                        hasNonMergeErrors = true;
                                    }
                                }
                            }

                            // If there were no merge errors and there were other errors, set the error flag.
                            // Note: This is done because all work items are flagged with a server error when a merge
                            //       conflict occurs.  Since we will be attempting to resolve the merge conflict,
                            //       we will not flag the error until the next save attempt.
                            if (!hasMergeErrors && hasNonMergeErrors) {
                                hasError = true;
                            }

                            // Try and start the next save.
                            tryStartNextSave(false);
                        } else {
                            // There is not an individual error per work item, so associate the error with each of the work items.
                            workItems.forEach((workItem: IWorkItem) => {
                                workItem.setError(error);
                            });

                            errorCallback?.call(service, error);
                        }
                    },
                    false /* Do not associate errors with the work items as this will be done after all of the batches have been completed */,
                    keepDeletedLinks,
                    ignoreWorkItemUpdateTelemetry
                );
            } else {
                if (hasError) {
                    // If the result has an error associated with it, store the error with the work item.
                    results.forEach((result: IWorkItemBulkSaveResult) => {
                        if (result.error) {
                            result.workItem.setError(result.error);
                        }
                    });

                    errorCallback?.call(this, this._createWorkItemBulkSaveFailedException(results));
                } else {
                    if (typeof callback === "function") {
                        const successResult: IWorkItemsBulkSaveSuccessResult = { workItems: workItems };
                        callback.call(service, successResult);
                    }
                }
            }
        };

        beginSave();
    }

    public beginGetWorkItemData(
        ids: number[],
        callback: (workItemData: IWorkItemData | IWorkItemData[]) => void,
        errorCallback?: (error: Error) => void,
        isDeleted?: boolean,
        includeHistory?: boolean,
        excludeFromUserRecentActivity?: boolean
    ) {
        let argumentError;

        if (ids.length === 0) {
            argumentError = new Error("Resources.BeginGetWorkItemArgumentNull");
        }

        if (ids) {
            for (let i = 0, l = ids.length; i < l; ++i) {
                // Ensure values are numbers
                ids[i] = +ids[i];

                if (ids[i] === undefined) {
                    argumentError = new Error("Resources.WorkItemIdUndefined");
                    break;
                } else if (isNaN(ids[i])) {
                    argumentError = new Error("Resources.WorkItemIdNaN");
                    break;
                } else if (ids[i] <= 0) {
                    argumentError = new Error("Resources.WorkItemIdOutOfRange");
                    break;
                }
            }
        }

        if (argumentError) {
            argumentError.name = "ArgumentException";
            // handleError(argumentError, errorCallback, this);

            // Ensure we are publishing telemetry on invalid arguments.  Seems that some clients may be handling the error
            // callback and not logging the error, and handleError will not publish telemetry if 'errorCallback' is a function.
            if (errorCallback) {
                // publishErrorToTelemetry(argumentError);
            }
            return;
        }

        const successCallback = (workItemPayload: any /* TODO: can be type, need to check in server */) => {
            if (workItemPayload && workItemPayload.length === 1) {
                callback.call(this, workItemPayload[0]);
            } else {
                callback.call(this, workItemPayload);
            }
        };

        this.witDataManager.beginGetWorkItemData(ids, isDeleted, !excludeFromUserRecentActivity, includeHistory).then(successCallback, errorCallback);
    }

    public async saveWorkItemLinksAsync(workItem: IWorkItem, links: WorkItemLink[], fieldsToClear?: CoreField[]): Promise<IWorkItemBulkSaveResult[]> {
        const changedWorkItemLinks: ILinkInfo[] = [];
        let error: IWorkItemSavingFailedError;
        const workItemUpdatePackages: IWorkItemUpdatePackage[] = [];
        const workItemUpdates: IWorkItemUpdate[] = [];
        let updatedWorkItems: IWorkItem[] = [];
        const results: IWorkItemBulkSaveResult[] = [];
        const tempIdMap: { [id: number]: number } = {};
        const workItemChanges: { [id: number]: IWorkItemChangedArgs } = {};
        const linkUpdates: ILinkUpdates = {};
        linkUpdates.addedLinks = [];

        for (const addedLink of links) {
            changedWorkItemLinks.push({
                command: LinkCommand.Add,
                sourceId: workItem.id,
                targetId: addedLink.getTargetId(),
                linkData: addedLink.linkData
            });

            linkUpdates.addedLinks.push(addedLink.linkData);
        }

        const tryFinish = () => {
            if (updatedWorkItems && updatedWorkItems.length === results.length) {
                for (const updatedWorkItem of updatedWorkItems) {
                    for (const link of updatedWorkItem.getLinks()) {
                        link.setState(true);
                    }
                }
                finish();
            }
        };

        const finish = () => {
            try {
                if (error) {
                    error = this._createWorkItemBulkSaveFailedException(results);
                    console.warn(this, error);
                }
                return results;
            } catch (exception) {
                throw exception;
            }
        };

        const workItemUpdate = workItem.getEmptyUpdateData();
        if (workItemUpdate.payload) {
            workItemUpdate.payload.links = linkUpdates;
        }

        const args: IWorkItemChangedArgs = {
            changedWorkItemLinks
        };

        if (workItemUpdate && workItemUpdate.payload) {
            workItemUpdates.push(workItemUpdate);
            workItemUpdatePackages.push(workItemUpdate.payload);

            const id = workItem.id > 0 ? workItem.id : workItem.tempId;

            workItemChanges[id] = args;
            updatedWorkItems.push(workItem);
        }

        try {
            const routeParams: IRouteParams = { controller: "wit", action: "updateWorkItems" };

            const requestParams: any = {
                updatePackage: JSON.stringify(workItemUpdatePackages)
            };
            try {
                const witDataSourceResponse: IDataSourceResponse<IWorkItemUpdateResult[]> = await new WorkItemStoreLevelWITDataSource(
                    this.pageContext
                ).getDataWithoutCache(routeParams, requestParams);
                const updateResults: IWorkItemUpdateResult[] = witDataSourceResponse.data;
                // what we get back is an update result, which may contain more changed fields than even our save changed
                if (updateResults.length === updatedWorkItems.length) {
                    // Check if any item in the batch is failed. If any item
                    // in the batch failed then none of the work items were saved
                    // even though some may say "Success"
                    // That "Success" status just means that workItem would have saved
                    // had the batch not contained an invalid work item
                    let batchFailed = false;
                    for (const res of updateResults) {
                        if (res.state !== WorkItemUpdateResultState.Success) {
                            batchFailed = true;
                            break;
                        }
                    }

                    for (let i = 0; i < updatedWorkItems.length; i++) {
                        const workItem = updatedWorkItems[i];
                        const updateResult = updateResults[i];

                        // Only consider this workitem update successful if the batch passsed
                        if (updateResult.state === WorkItemUpdateResultState.Success && !batchFailed) {
                            await workItem.takeUpdateResult(this.pageContext, updateResult, workItemUpdates[i], {
                                ignoreFieldUpdates: true,
                                fieldsToClear
                            });

                            results.push({ workItem: workItem });
                            tempIdMap[workItem.tempId] = workItem.id;
                        } else if (updateResult.state !== WorkItemUpdateResultState.Success) {
                            if (updateResult.error) {
                                workItem.setError(updateResult.error);
                            }
                        } else {
                            results.push({ workItem: workItem });
                        }
                        tryFinish();
                    }
                } else {
                    updatedWorkItems.forEach((workItem: IWorkItem) => {
                        workItem.setError({ name: "SyncError", message: "Server data does not match with what client sent." });
                    });

                    finish();
                }
            } catch (error) {
                updatedWorkItems.forEach((workItem: IWorkItem) => {
                    workItem.setError(error);
                });

                finish();
            }
        } catch (exception) {
            throw exception;
        }

        return results;
    }

    /**
     * Begin saving the provided work items.  If saving any of the work items fails, no work items are saved.
     * See beginSaveWorkItemsBatch if you would like all workitems without error to be saved.
     * @param workItems Work items to be saved
     * @param tempIdMap Map of tempId to real work item id
     * @param serverFieldUpdates Changes which occurred to the work items due to a merge operation.  Indexed by work item id
     * @param callback
     * @param errorCallback
     * @param storeErrors Flag indicating if errors should be associated with the work items
     * @param keepDeletedLinks Flag indicating skip of cleaning up deleted links from the list of workItem links
     */
    private async _beginSaveWorkItemsInternal(
        workItems: IWorkItem[],
        tempIdMap: { [id: number]: number },
        serverFieldUpdates: { [id: number]: { [key: string]: IField } },
        callback: (result: IWorkItemsBulkSaveSuccessResult) => void,
        errorCallback?: (error: IWorkItemSavingFailedError) => void,
        storeErrors: boolean = true,
        keepDeletedLinks?: boolean,
        ignoreWorkItemUpdateTelemetry?: boolean
    ) {
        let updatedWorkItems: IWorkItem[];
        let error: IWorkItemSavingFailedError;
        const results: IWorkItemBulkSaveResult[] = [];
        const workItemChanges: { [id: number]: IWorkItemChangedArgs } = {};

        const markSaveFlag = (workItems: IWorkItem[], saving: boolean) => {
            for (const curWorkItem of workItems) {
                const workItemChangedArgs = workItemChanges[curWorkItem.id] ? workItemChanges[curWorkItem.id] : workItemChanges[curWorkItem.tempId];

                // merge field changes with any changes that came out of the 'merge' operation, if any
                if (workItemChangedArgs && serverFieldUpdates[curWorkItem.id]) {
                    Object.assign(workItemChangedArgs.changedFields!, serverFieldUpdates[curWorkItem.id]);
                }

                // TODO:Replat Events and status behavior
                // (<any>curWorkItem)._setSavingStatus(saving, workItemChangedArgs);
                // (<any>curWorkItem)._setValidatingStatus(false);

                curWorkItem.saving.value = saving;
            }
        };

        const finish = () => {
            try {
                if (error) {
                    error = this._createWorkItemBulkSaveFailedException(results);
                    errorCallback?.call(this, error);
                } else {
                    const successResult: IWorkItemsBulkSaveSuccessResult = { workItems: workItems };
                    callback.call(this, successResult);
                }
            } finally {
                markSaveFlag(updatedWorkItems, false);
            }
        };

        const setError = (workItem: IWorkItem, workItemError: unknown) => {
            let errorMessage: string | undefined = undefined;
            if (typeof workItemError === "string") {
                errorMessage = workItemError;
            } else if (
                workItemError &&
                typeof workItemError === "object" &&
                "message" in workItemError &&
                typeof (workItemError as any).message === "string"
            ) {
                errorMessage = (workItemError as any).message;
            }
            error = new Error(errorMessage);

            // Associate the error with the work item.
            if (storeErrors) {
                workItem.setError(error);
            }

            results.push({ workItem: workItem, error: workItemError });
        };

        const tryFinish = () => {
            if (updatedWorkItems && updatedWorkItems.length === results.length) {
                for (const updatedWorkItem of updatedWorkItems) {
                    for (const link of updatedWorkItem.getLinks()) {
                        link.setState(true);
                    }
                }
                finish();
            }
        };

        const getFieldsDictionaryOfChanges = (workItem: IWorkItem, changedFields: IFieldDataDictionary): { [id: string]: IField } => {
            // Retreives a field refname => Field mapping for fields that have changed, converting from the field data dictionary
            const fields: { [id: string]: IField } = {};

            for (const fieldId in changedFields) {
                if (changedFields.hasOwnProperty(fieldId)) {
                    const changedFieldId = Number(fieldId);
                    const changedField = workItem.fieldMapById[changedFieldId];

                    if (changedField) {
                        fields[changedField.fieldDefinition.referenceName] = changedField;

                        // If area/iteration id changed, add area/iteration path
                        if (changedFieldId === CoreField.IterationId) {
                            fields[CoreFieldRefNames.IterationPath] = workItem.fieldMapById[CoreField.IterationPath];
                        } else if (changedFieldId === CoreField.AreaId) {
                            fields[CoreFieldRefNames.AreaPath] = workItem.fieldMapById[CoreField.AreaPath];
                        }
                    }
                }
            }

            return fields;
        };

        const convertUpdateToArgs = (workItem: IWorkItem, update: IWorkItemUpdate): IWorkItemChangedArgs => {
            // Creates an IWorkItemChangedArgs for the specified work item and set of changes made to the work item.
            const changedWorkItemLinks: ILinkInfo[] = [];
            if (update.deletedLinks) {
                for (const deletedLink of update.deletedLinks) {
                    if (deletedLink instanceof WorkItemLink) {
                        changedWorkItemLinks.push({
                            command: LinkCommand.Remove,
                            sourceId: workItem.id,
                            targetId: deletedLink.getTargetId(),
                            linkData: deletedLink.linkData
                        });
                    }
                }
            }
            if (update.addedLinks) {
                for (const addedLink of update.addedLinks) {
                    if (addedLink instanceof WorkItemLink) {
                        // Check if this work item was already saved. If it was, then update link data to use the real id
                        // It is possible to get an error if a parent work item was saved in a previous batch while
                        // the child is saved in a newer batch because the parent work item will not have a temp id anymore.
                        if (tempIdMap.hasOwnProperty(addedLink.getTargetId())) {
                            addedLink.linkData.ID = tempIdMap[addedLink.getTargetId()];
                        }
                        changedWorkItemLinks.push({
                            command: LinkCommand.Add,
                            sourceId: workItem.id,
                            targetId: addedLink.getTargetId(),
                            linkData: addedLink.linkData
                        });
                    }
                }
            }

            let typeChanged = false;
            if (update.payload?.fields) {
                typeChanged = !!update.payload.fields[DalFields.AreaPathId];
            }
            const args: IWorkItemChangedArgs = {
                typeChanged: workItem.hasWorkItemTypeChanged() ?? typeChanged,
                projectChanged: workItem.hasTeamProjectChanged(),
                changedFields: update.payload && update.payload.fields ? getFieldsDictionaryOfChanges(workItem, update.payload.fields) : {},
                changedWorkItemLinks,
                firstSave: workItem.id == 0
            };

            return args;
        };

        if (workItems && workItems.length > 0) {
            const workItemUpdatePackages: IWorkItemUpdatePackage[] = [];
            const workItemUpdates: IWorkItemUpdate[] = [];

            updatedWorkItems = [];

            let hasRejectedPromises = false;
            for (let i = 0; i < workItems.length; i++) {
                const workItem = workItems[i];

                try {
                    await workItem.waitForPromisesResolving();
                } catch (exception) {
                    setError(workItem, exception);
                    hasRejectedPromises = true;
                }

                // TODO:Replat Fire these events
                // workItem.fireWorkItemPreSave();

                if (workItem.dirty.value) {
                    const workItemUpdate = workItem.getUpdateData();

                    if (workItemUpdate && workItemUpdate.payload) {
                        workItemUpdates.push(workItemUpdate);
                        workItemUpdatePackages.push(workItemUpdate.payload);

                        const id = workItem.id > 0 ? workItem.id : workItem.tempId;

                        workItemChanges[id] = convertUpdateToArgs(workItem, workItemUpdate);
                        updatedWorkItems.push(workItem);
                    }
                }
            }

            if (updatedWorkItems.length > 0 && !hasRejectedPromises) {
                markSaveFlag(updatedWorkItems, true);

                try {
                    // first we save the work item
                    const routeParams: IRouteParams = { controller: "wit", action: "updateWorkItems" };

                    const requestParams: any = {
                        updatePackage: JSON.stringify(workItemUpdatePackages)
                    };
                    try {
                        const witDataSourceResponse: IDataSourceResponse<IWorkItemUpdateResult[]> = await new WorkItemStoreLevelWITDataSource(
                            this.pageContext
                        ).getDataWithoutCache(routeParams, requestParams);
                        const updateResults: IWorkItemUpdateResult[] = witDataSourceResponse.data;
                        // what we get back is an update result, which may contain more changed fields than even our save changed
                        if (updateResults.length === updatedWorkItems.length) {
                            // Check if any item in the batch is failed. If any item
                            // in the batch failed then none of the work items were saved
                            // even though some may say "Success"
                            // That "Success" status just means that workItem would have saved
                            // had the batch not contained an invalid work item
                            let batchFailed = false;
                            for (const res of updateResults) {
                                if (res.state !== WorkItemUpdateResultState.Success) {
                                    batchFailed = true;
                                    break;
                                }
                            }

                            for (let i = 0; i < updatedWorkItems.length; i++) {
                                const workItem = updatedWorkItems[i];
                                const updateResult = updateResults[i];

                                // Only consider this workitem update successful if the batch passsed
                                if (updateResult.state === WorkItemUpdateResultState.Success && !batchFailed) {
                                    // TODO:Replat getWorkItemTypeExtensions
                                    // this.beginGetWorkItemTypeExtensions(
                                    //     updateResult.currentExtensions,
                                    //     (extensions: IWorkItemTypeExtension[]) => {
                                    //         // if we got a good result, then we do _takeUpdateResult, which basically is responsible for notifying other pieces of the code about the fields that have changed
                                    //         workItem._takeUpdateResult(updateResult, workItemUpdates[i], extensions);

                                    //         results.push({ workItem: workItem });
                                    //         tempIdMap[workItem.tempId] = workItem.id;

                                    //         tryFinish();
                                    //     },
                                    //     (error: any) => {
                                    //         setError(workItem, error);
                                    //         tryFinish();
                                    //     }
                                    // );

                                    await workItem.takeUpdateResult(this.pageContext, updateResult, workItemUpdates[i], {
                                        keepDeletedLinks,
                                        ignoreTelemetry: ignoreWorkItemUpdateTelemetry
                                    });

                                    results.push({ workItem: workItem });
                                    tempIdMap[workItem.tempId] = workItem.id;

                                    tryFinish();
                                } else if (updateResult.state !== WorkItemUpdateResultState.Success) {
                                    if (updateResult.error) {
                                        setError(workItem, updateResult.error);
                                    }
                                    tryFinish();
                                } else {
                                    results.push({ workItem: workItem });
                                    tryFinish();
                                }
                            }
                        } else {
                            updatedWorkItems.forEach((workItem: IWorkItem) => {
                                setError(workItem, { message: "Server data does not match with what client sent." });
                            });

                            finish();
                        }
                    } catch (error) {
                        updatedWorkItems.forEach((workItem: IWorkItem) => {
                            setError(workItem, error);
                        });

                        finish();
                    }
                } catch (exception) {
                    markSaveFlag(updatedWorkItems, false);

                    throw exception;
                }
            } else {
                finish();
            }
        } else {
            finish();
        }
    }

    /**
     * Begins merging the provided work item with the server version of the work item.  Callback will be called with
     * the work item when the merge completes
     * @param workItem Work item to try and merge
     * @param completedCallback
     * @param errorCallback
     */
    private _beginTryMergeWorkItem(
        workItem: IWorkItem,
        callback: (workItem: IWorkItem, changedFields: { [fieldName: string]: IField }) => void,
        errorCallback?: (workItem: IWorkItem) => void
    ) {
        const mergedWorkItemUpdates: { [id: number]: any } = {}; // Used to track updates which should be made to the merged work item.  The property name is the fieldId and the value is the field value.

        // Load up the updated work item data from the server.
        this.beginGetWorkItemData(
            [workItem.id],
            (workItemPayload: IWorkItemData) => {
                // Check for conflicts in the updates.
                if (this._hasConflicts(workItem, workItemPayload, mergedWorkItemUpdates) && errorCallback) {
                    // There are conflicts so bail.
                    errorCallback(workItem);
                    return;
                }

                // Get the set of fields that changed between the work item we have locally and the server version of the work item.
                const changedFields: { [fieldName: string]: IField } = this._getChangedFieldsForMerge(workItem, workItemPayload);

                // There were no conflicts, so merge the payload into the work item.
                this._mergeWorkItem(workItem, workItemPayload);

                // Run the rules on the work item.
                workItem.evaluate();

                // Re-apply the field updates to the work item (rules may have overridden user values).
                for (const fieldId in mergedWorkItemUpdates) {
                    if (mergedWorkItemUpdates.hasOwnProperty(fieldId)) {
                        workItem.setFieldValueById(Number.parseInt(fieldId), mergedWorkItemUpdates[fieldId]);
                    }
                }

                // If the work item is valid, then merge was successful
                if (workItem.isValid()) {
                    callback(workItem, changedFields);
                } else if (errorCallback) {
                    errorCallback(workItem);
                }
            },
            () => {
                // There was an error getting the updated work item data so fail the merge.
                errorCallback && errorCallback(workItem);
            },
            false,
            true
        );
    }

    /**
     * Merge the payload data into the provided work item
     * @param workItem Work item to merge data into
     * @param workItemPayload Work item payload data to merge into the work item
     */
    private _mergeWorkItem(workItem: IWorkItem, workItemPayload: IWorkItemData) {
        let links: ILinkUpdates;

        // Get a copy of the added and removed links so we can restore them after the update.
        links = workItem.getLinkUpdates();

        // Merge the server data into the work item ensuring
        // that the fields are reset.
        workItem.load(workItemPayload, true);

        // Restore added and removed links.
        workItem.restoreLinkUpdates(links);
    }

    private _getChangedFieldsForMerge(workItem: IWorkItem, workItemPayload: IWorkItemData): { [fieldName: string]: IField } {
        const changedFields: { [fieldName: string]: IField } = {};
        const fields: IFieldDataDictionary = workItemPayload.fields;

        for (const fieldId in fields) {
            if (fields.hasOwnProperty(fieldId)) {
                const baselineField = workItem.getFieldById(Number.parseInt(fieldId));

                if (baselineField) {
                    // Get the original value of the field and the server version of the field.
                    const originalValue = workItem.getFieldValueById(+fieldId, true);
                    const serverValue = convertFieldValueToExternal(workItemPayload.fields[fieldId], baselineField.fieldDefinition.type);

                    if (!compareFieldValues(originalValue, serverValue, false)) {
                        // This field value is different on the server vs the original value on the client.
                        changedFields[baselineField.fieldDefinition.referenceName] = baselineField;
                    }
                }
            }
        }

        return changedFields;
    }

    /**
     * Check the provided work item against the payload for conflicts
     * @param workItem Work item to check
     * @param workItemPayload Work item payload data to check the work item against
     * @param mergedWorkItemUpdates Maps the field ID to the updated field value which should be set on the merged work item
     */
    private _hasConflicts(workItem: IWorkItem, workItemPayload: IWorkItemData, mergedWorkItemUpdates: { [id: number]: any }): boolean {
        let originalValue: any;
        let serverValue: any;
        let baselineField: IField | undefined;
        let baselineValue: any;

        // Check for conflicts in the updates.
        const fieldUpdates = workItem.getFieldUpdates();
        for (const fieldIdStr in fieldUpdates) {
            if (fieldUpdates.hasOwnProperty(fieldIdStr)) {
                // Ensure the field ID is a number (will always be a string because it is coming from the objects property name).
                const fieldId = Number(fieldIdStr);

                baselineField = workItem.getFieldById(fieldId);

                // If the field with conflicting edit doesn't exist on the target workItemType, ignore this field and continue
                if (baselineField) {
                    // Get the original value of the field and the server version of the field.
                    originalValue = workItem.getFieldValueById(fieldId, true);
                    serverValue = convertFieldValueToExternal(workItemPayload.fields[fieldId], baselineField.fieldDefinition.type);

                    // If the value was changed by the user, then check for a conflict.
                    // NOTE: Values not changed by the user (chanded by rule) are ignored because the field updates will be applied to the work
                    //       item again in resolving the merge conflict and the rules will set the value again.
                    if (isUserChange(baselineField, fieldUpdates[fieldIdStr])) {
                        // If currentField is updated (client changes) - we need to get current client's value (not original)
                        baselineValue = workItem.getFieldValueById(fieldId, false);

                        // If the original value does not match the server value, then there is a conflict.
                        if (
                            fieldId != CoreField.History && // History can nevery cause a conflict.
                            !compareFieldValues(originalValue, serverValue, false)
                        ) {
                            // If the value that the user changed the field to does not match the value on the server,
                            // then bail since we can not resolve the conflict.
                            if (!compareFieldValues(baselineValue, serverValue, false)) {
                                // There are conflicts so bail.
                                return true;
                            }
                        } else {
                            // The field was changed by the user, but not changed on the server so store off
                            // the value to update the merged work item with.
                            mergedWorkItemUpdates[fieldId] = baselineValue;

                            // If the change is to an area or iteration ID field, also capture the update to the corresponding path field.
                            // NOTE: This is to workaround an issue in the rule evaluation where these calculated fields will be
                            //       flaged as "setByRule" and if this is the only update in the work item, the work item will not be considered
                            //       dirty and will not be saved.
                            // TODO: [apatters] Follow up with Ahmet when he is back in the office to see if we can change the rule evaluation back to
                            //       not flag computed field values as set by rule (was reverted in changeset 92265, but it is not clear why from the
                            //       bugs which were being fixed in the changeset).  Changeset 94466 fixed the original issue that this reversion caused
                            //       but it is dependend upon order of the fields and with the updates to the merge on save algorithm, this fix no longer
                            //       works without the below workaround.
                            if (fieldId === CoreField.IterationId) {
                                mergedWorkItemUpdates[CoreField.IterationPath] = workItem.getFieldValueById(CoreField.IterationPath);
                            } else if (fieldId === CoreField.AreaId) {
                                mergedWorkItemUpdates[CoreField.AreaPath] = workItem.getFieldValueById(CoreField.AreaPath);
                            }
                        }
                    }
                }
            }
        }

        return false;
    }

    private _createWorkItemBulkSaveFailedException(results: IWorkItemBulkSaveResult[]): IWorkItemSavingFailedError {
        const error: IWorkItemSavingFailedError = new Error(Resources.WorkItemBulkSaveFailed);
        error.name = Exceptions.WorkItemBulkSaveException;
        error.results = results;

        return error;
    }
}

const serviceName = "IWorkItemSavingService";
Services.add(serviceName, { serviceFactory: WorkItemSavingService });
